import React, { useState, useEffect, useCallback } from 'react';
import { accounting } from '../utils/money';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Download } from 'lucide-react';
import { drawRowWashStatement, drawRowWashComparison, drawPeriodGrid } from '../utils/pnlStatementPdf';
import './PnLStatement.css';
import ClearPnlWorkspace from './ClearPnlWorkspace';

const API = '';
const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

/* ---- Formatters ---- */
const money = (v) => accounting(v);

function pct(v) {
  if (v == null) return '—';
  return (v * 100).toFixed(1) + '%';
}

/* Boxed percentage chip (same-line, solid) */
function PctChip({ value }) {
  if (value == null) return '—';
  const neg = Number(value) < 0;
  return <span className={`pnlg-chip${neg ? ' pnlg-chip-neg' : ''}`}>{pct(value)}</span>;
}

function isNegVal(v) {
  if (v == null) return false;
  return Number(v) < 0;
}

function getCurrentQuarter() {
  return QUARTERS[Math.floor(new Date().getMonth() / 3)];
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Spelled out for the PDF statement's period line, where "Jul 2026" reads clipped.
const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// 'YYYY-MM' -> 'Jul 2026'
function monthLabel(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  return `${MONTH_NAMES[(m - 1) % 12]} ${y}`;
}
// the month before a 'YYYY-MM'
function prevMonth(ym) {
  const [y, m] = ym.split('-').map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}
// Signed deltas, e.g. +1,200.00 / -800.00.
// The sign is a plain ASCII hyphen, never U+2212: jsPDF's standard fonts have no
// glyph for the typographic minus, and dropped it silently — every negative
// variance printed as a stray quote with the digits spaced out, losing the sign
// itself, so a fall read as a rise.
function deltaMoney(d) {
  if (d == null) return '—';
  if (d === 0) return '0.00';
  return (d > 0 ? '+' : '-') + Number(Math.abs(d)).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function deltaPct(p) {
  if (p == null) return '—';
  return (p > 0 ? '+' : p < 0 ? '-' : '') + Math.abs(p).toFixed(1) + '%';
}

/* ---- highlight keys ---- */
const BIG_KEYS = new Set(['gross_profit', 'ebitda', 'pbt', 'pat']);

/* Lines inside the COGS section where a RISING share of revenue is good.
   Everything else there is a cost, where a rising share is bad. */
const MARGIN_KEYS = new Set(['gross_profit']);

/**
 * COGS variance: the swing in share of revenue, in percentage points, signed so
 * that "better" is positive. A cost line going 4% → 5% of revenue is worse, so
 * it reads −1.0%; Gross Profit going 42% → 46% is better, so it reads +4.0%.
 * Returns null unless both months have a percentage to compare.
 */
function ratioSwing(aPct, bPct, key) {
  if (aPct == null || bPct == null) return null;
  const rise = (Number(bPct) - Number(aPct)) * 100;
  return MARGIN_KEYS.has(key) ? rise : -rise;
}

/* ======================================================
   GENERIC PERIOD GRID VIEW
   columns = [{ label, data, highlight }]
   highlight: boolean — whether to give this column the amber highlight
   ====================================================== */

/**
 * Build a flat list of row descriptors from the first available data sections.
 */
function buildRowList(sections, expandedKeys) {
  const rows = [];
  for (const section of sections) {
    rows.push({ kind: 'section', key: `sec_${section.key}`, label: section.name });
    for (const line of section.lines) {
      if (line.kind === 'category') {
        const isOpen = expandedKeys.has(line.key) && !!(line.children && line.children.length);
        rows.push({ kind: 'category', key: line.key, label: line.label, section: section.key, hasChildren: !!(line.children && line.children.length), open: isOpen });
        if (isOpen) {
          for (let i = 0; i < line.children.length; i++) {
            const child = line.children[i];
            rows.push({ kind: 'child', key: child.key, label: child.label, section: section.key, parentKey: line.key, last: i === line.children.length - 1 });
          }
        }
      } else if (line.kind === 'computed') {
        rows.push({ kind: 'computed', key: line.key, label: line.label, section: section.key });
      } else {
        rows.push({ kind: 'input', key: line.key, label: line.label, section: section.key });
      }
    }
  }
  return rows;
}

/**
 * Build a lookup from a data object: { [lineKey]: { actual, pctRev } }
 */
function buildColLookup(data) {
  const map = {};
  if (!data) return map;
  for (const section of (data.sections || [])) {
    for (const line of (section.lines || [])) {
      if (line && line.key) {
        map[line.key] = { actual: line.actual, forecast: line.forecast, pctRev: line.pctRev, variance: line.variance };
      }
      for (const child of (line.children || [])) {
        if (child && child.key) {
          map[child.key] = { actual: child.actual, forecast: child.forecast, pctRev: child.pctRev, variance: child.variance };
        }
      }
    }
  }
  return map;
}

function hasRecordedValue(data, field) {
  return !!data?.sections?.some(section => section.lines?.some(line => (
    line.kind !== 'computed' && (line[field] != null || line.children?.some(child => child[field] != null))
  )));
}

/**
 * Pick the single "primary" column to summarise in the simple view:
 * the total column if present, else the highlighted one, else the last.
 */
function pickPrimaryColumn(columns) {
  if (!columns || !columns.length) return null;
  return columns.find(c => c.isTotal && c.data) || columns.find(c => c.highlight && c.data) || [...columns].reverse().find(c => c.data) || columns[columns.length - 1];
}

/**
 * Reduce a full P&L column to the important-only waterfall lines.
 * Deductions are shown as negative magnitude; subtotals keep their computed sign.
 * Returns [{ label, kind: 'sub'|'key'|'ded'|'final', amount, pct }] or null.
 */
function buildSimpleLines(data) {
  if (!data || !data.sections) return null;
  const m = buildColLookup(data);
  const hasActual = hasRecordedValue(data, 'actual');
  const hasBudget = hasRecordedValue(data, 'forecast');
  const val = (k) => (hasActual && m[k] ? m[k].actual : null);
  const budget = (k) => (hasBudget && m[k] ? m[k].forecast : null);
  const margin = (k) => (m[k] ? m[k].pctRev : null);

  return [
    { label: 'Total Revenue', kind: 'sub', amount: val('total_revenue'), budget: budget('total_revenue'), pct: margin('total_revenue') ?? (hasActual ? 1 : null) },
    { label: 'Cost of Goods Sold', kind: 'ded', amount: val('total_cogs'), budget: budget('total_cogs'), pct: margin('total_cogs') },
    { label: 'Gross Profit', kind: 'key', amount: val('gross_profit'), budget: budget('gross_profit'), pct: margin('gross_profit') },
    { label: 'Profit Sharing', kind: 'ded', amount: val('total_profit_sharing'), budget: budget('total_profit_sharing'), pct: margin('total_profit_sharing') },
    { label: 'Operating Expenses', kind: 'ded', amount: val('total_opex'), budget: budget('total_opex'), pct: margin('total_opex') },
    { label: 'EBITDA', kind: 'key', amount: val('ebitda'), budget: budget('ebitda'), pct: margin('ebitda') },
    { label: 'Interest & Depreciation', kind: 'ded', amount: val('total_below_ebitda'), budget: budget('total_below_ebitda'), pct: margin('total_below_ebitda') },
    { label: 'PBT (Profit Before Tax)', kind: 'key', amount: val('pbt'), budget: budget('pbt'), pct: margin('pbt') },
    { label: 'Tax (handled annually)', kind: 'ded', amount: val('tax'), budget: budget('tax'), pct: margin('tax') },
    { label: 'Net Profit (PAT)', kind: 'final', amount: val('pat'), budget: budget('pat'), pct: margin('pat') },
  ];
}

function signedSimpleValue(line, field) {
  const value = line[field];
  if (value == null) return null;
  return line.kind === 'ded' ? -Math.abs(Number(value)) : Number(value);
}

function simpleVariance(line) {
  const actual = signedSimpleValue(line, 'amount');
  const budget = signedSimpleValue(line, 'budget');
  return actual == null || budget == null ? null : actual - budget;
}

function simpleKpis(data) {
  const lines = buildSimpleLines(data);
  if (!lines) return [];
  const byLabel = Object.fromEntries(lines.map(line => [line.label, line]));
  const revenue = byLabel['Total Revenue'];
  const grossProfit = byLabel['Gross Profit'];
  const ebitda = byLabel.EBITDA;
  const netProfit = byLabel['Net Profit (PAT)'];
  const actualGrossMargin = revenue.amount ? grossProfit.amount / revenue.amount : null;
  const budgetGrossMargin = revenue.budget ? grossProfit.budget / revenue.budget : null;
  return [
    { label: 'Revenue', value: revenue.amount, budget: revenue.budget, format: 'money', tone: 'revenue' },
    { label: 'Gross margin', value: actualGrossMargin, budget: budgetGrossMargin, format: 'percent', tone: 'margin' },
    { label: 'EBITDA', value: ebitda.amount, budget: ebitda.budget, format: 'money', tone: 'ebitda' },
    { label: 'Net profit', value: netProfit.amount, budget: netProfit.budget, format: 'money', tone: 'profit' },
  ];
}

function KpiCards({ primary }) {
  const metrics = simpleKpis(primary?.data);
  if (!metrics.length) return null;
  return (
    <div className="pnl-kpis" aria-label={`${primary.label} financial highlights`}>
      {metrics.map(metric => {
        const delta = metric.value == null || metric.budget == null ? null : metric.value - metric.budget;
        const valueText = metric.value == null
          ? '—'
          : metric.format === 'percent' ? pct(metric.value) : `RM ${money(metric.value)}`;
        const deltaText = metric.value == null
          ? 'Actual not entered'
          : metric.budget == null
            ? 'Budget not set'
            : metric.format === 'percent'
            ? `${delta > 0 ? '+' : delta < 0 ? '-' : ''}${Math.abs(delta * 100).toFixed(1)} pts vs budget`
            : `RM ${money(Math.abs(delta))} ${delta >= 0 ? 'above' : 'below'} budget`;
        return (
          <section className={`pnl-kpi ${metric.tone}${Number(metric.value) < 0 ? ' negative' : ''}`} key={metric.label}>
            <span className="k-l">{metric.label}</span>
            <strong className="k-v">{valueText}</strong>
            <span className={`k-m${delta == null || delta === 0 ? '' : delta > 0 ? ' favorable' : ' unfavorable'}`}>{deltaText}</span>
          </section>
        );
      })}
    </div>
  );
}

/**
 * SimplePnl — important-only statement (Design A) for one period column.
 */
function SimplePnl({ columns }) {
  const primary = pickPrimaryColumn(columns);
  const lines = primary ? buildSimpleLines(primary.data) : null;
  if (!lines) return <div className="pnl-loading"><div className="pnl-spinner" /><p>No data</p></div>;
  const pctText = (v, abs) => (v == null ? '—' : pct(abs ? Math.abs(v) : v));
  return (
    <div className="pnl-simple">
      <div className="ps-head">
        <div><span className="ps-title">Summary statement</span><span className="ps-caption">Actual performance against budget</span></div>
        <span className="ps-period">{primary.label}</span>
      </div>
      <div className="ps-scroll">
        <table className="ps-table">
          <thead><tr><th>Account</th><th>Actual (RM)</th><th>Budget (RM)</th><th>Variance</th><th>% Revenue</th></tr></thead>
          <tbody>
            {lines.map((line) => {
              const actual = signedSimpleValue(line, 'amount');
              const budget = signedSimpleValue(line, 'budget');
              const variance = simpleVariance(line);
              const resultNegative = line.kind !== 'ded' && Number(line.amount) < 0;
              return (
                <tr className={`ps-line ${line.kind}${line.kind === 'final' ? ' ps-final' : ''}${resultNegative ? ' is-loss' : ''}`} key={line.label}>
                  <th scope="row" className="ps-lbl">{line.label}</th>
                  <td className={`ps-amt num${resultNegative ? ' neg' : ''}`}>{actual == null ? '—' : money(actual)}</td>
                  <td className={`ps-budget num${line.kind !== 'ded' && Number(line.budget) < 0 ? ' neg' : ''}`}>{budget == null ? '—' : money(budget)}</td>
                  <td className={`ps-var num${variance == null || variance === 0 ? '' : variance > 0 ? ' favorable' : ' unfavorable'}`}>{deltaMoney(variance)}</td>
                  <td className={`ps-pc${line.kind !== 'ded' && Number(line.pct) < 0 ? ' neg' : ''}`}>{pctText(line.pct, line.kind === 'ded')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="ps-footnote">Amounts in parentheses are deductions. A positive variance is favourable against budget.</p>
    </div>
  );
}

/**
 * ComparisonGrid — two months side by side with a single Variance column.
 * Each month shows its RM amount plus that line's share of the month's total
 * revenue (% Rev). COGS lines show % change; all other sections show the RM
 * (absolute) change.
 */
function ComparisonGrid({ colA, colB }) {
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const toggleExpand = useCallback((key) => {
    setExpandedKeys(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }, []);

  if (!colA || !colB || !colA.data || !colB.data) return <div className="pnl-loading"><div className="pnl-spinner" /><p>Loading comparison…</p></div>;
  const structureData = [colA, colB].map(c => c && c.data).find(d => d && d.sections);
  if (!structureData) return <div className="pnl-loading"><div className="pnl-spinner" /><p>No data</p></div>;
  const rows = buildRowList(structureData.sections, expandedKeys);
  const la = buildColLookup(colA.data), lb = buildColLookup(colB.data);

  const dirClass = (d) => (d == null || d === 0 ? '' : d > 0 ? ' cmp-up' : ' cmp-down');

  return (
    <div className="pnlg-scroll">
      <table className="pnlg-table cmp-table">
        <thead>
          <tr>
            <th className="pnlg-desc-th">Description</th>
            <th className="cmp-col">{colA.label}</th>
            <th className="cmp-col cmp-pctcol">% Rev</th>
            <th className="cmp-col cmp-colB">{colB.label}</th>
            <th className="cmp-col cmp-colB cmp-pctcol">% Rev</th>
            <th className="cmp-col cmp-var">Variance</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            if (row.kind === 'section') {
              return <tr key={row.key} className="pnlg-section"><td colSpan={6}>{row.label}</td></tr>;
            }
            const aV = (la[row.key] || {}).actual;
            const bV = (lb[row.key] || {}).actual;
            const aPct = (la[row.key] || {}).pctRev;
            const bPct = (lb[row.key] || {}).pctRev;
            const hasA = aV != null, hasB = bV != null;
            const delta = (hasA || hasB) ? ((Number(bV) || 0) - (Number(aV) || 0)) : null;
            // COGS lines compare the two months' share of revenue (percentage points);
            // every other section (Revenue/Sales, Operating Expenses/Fixed, etc.) shows
            // the absolute RM movement.
            const showPct = row.section === 'cogs';
            const swing = showPct ? ratioSwing(aPct, bPct, row.key) : null;
            const changeVal = showPct ? swing : delta;
            const changeText = showPct ? deltaPct(swing) : deltaMoney(delta);
            const rowCls = row.kind === 'computed' ? 'cmp-row cmp-computed'
              : row.kind === 'category' ? 'cmp-row cmp-cat'
              : row.kind === 'child' ? 'cmp-row cmp-child' : 'cmp-row';
            return (
              <tr key={row.key} className={rowCls}>
                <td className="pnlg-desc">
                  {row.kind === 'category' && row.hasChildren ? (
                    <span className="pnlg-expand" onClick={() => toggleExpand(row.key)}>
                      <span className="pnlg-chev">{expandedKeys.has(row.key) ? '▾' : '▸'}</span>{row.label}
                    </span>
                  ) : row.kind === 'child' ? <span className="pnlg-child-label">{row.label}</span> : row.label}
                </td>
                <td className={`pnlg-amt${isNegVal(aV) ? ' pnlg-neg' : ''}`}>{hasA ? money(aV) : '—'}</td>
                <td className="pnlg-amt cmp-pctcol">{hasA ? pct(aPct) : '—'}</td>
                <td className={`pnlg-amt cmp-colB${isNegVal(bV) ? ' pnlg-neg' : ''}`}>{hasB ? money(bV) : '—'}</td>
                <td className="pnlg-amt cmp-colB cmp-pctcol">{hasB ? pct(bPct) : '—'}</td>
                <td className={`pnlg-amt cmp-delta${dirClass(changeVal)}`}>{changeText}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Month + year picker for comparison mode; value is 'YYYY-MM'. */
function MonthYearPicker({ value, onChange, years }) {
  const [y, m] = value.split('-').map(Number);
  return (
    <span className="cmp-pick">
      <select value={m} onChange={(e) => onChange(`${y}-${String(+e.target.value).padStart(2, '0')}`)}>
        {MONTH_NAMES.map((nm, i) => <option key={i} value={i + 1}>{nm}</option>)}
      </select>
      <select value={y} onChange={(e) => onChange(`${e.target.value}-${String(m).padStart(2, '0')}`)}>
        {years.map((yy) => <option key={yy} value={yy}>{yy}</option>)}
      </select>
    </span>
  );
}

/**
 * PeriodGrid — generic grid renderer.
 * columns: [{ label: string, data: {sections,...}|null, highlight: bool }]
 * The last column with highlight=true will get a "total column" visual treatment.
 */

function actualStatementSections(columns, sections) {
  const budgetOnly = new Set(['rev_wnexus_combined', 'ps_wnexus_combined', 'cogs_sales_expenses']);
  return sections.map(section => ({...section, lines: section.lines.filter(line => !budgetOnly.has(line.key) || columns.some(col => col.data?.sections?.some(s => s.lines.some(l => l.key === line.key && l.actual != null))))}));
}

function PeriodGrid({ columns, search = '' }) {
  const [expandedKeys, setExpandedKeys] = useState(new Set());

  const toggleExpand = useCallback((key) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // Use first available data for row structure
  const structureData = columns.map(c => c.data).find(d => d && d.sections);
  if (!structureData) return <div className="pnl-loading"><div className="pnl-spinner" /><p>No data</p></div>;

  const allSections = actualStatementSections(columns, structureData.sections || []);
  const query = search.trim().toLowerCase();
  const rows = buildRowList(allSections, query ? new Set(allSections.flatMap(s=>s.lines.filter(l=>l.children?.length).map(l=>l.key))) : expandedKeys).filter(row => !query || row.kind === 'section' || row.label.toLowerCase().includes(query));

  // Build per-column lookup
  const lookups = columns.map(col => buildColLookup(col.data));

  const numCols = columns.length;
  // Total table columns: 1 (Description) + 2*numCols
  const totalCols = 1 + 2 * numCols;

  const getCell = (colIdx, lineKey) => {
    return (lookups[colIdx] && lookups[colIdx][lineKey]) || { actual: null, pctRev: null };
  };

  const renderDataCells = (row) => {
    return columns.map((col, cIdx) => {
      const cell = getCell(cIdx, row.key);
      const neg = isNegVal(cell.actual);
      const isTotal = col.isTotal;
      return (
        <React.Fragment key={cIdx}>
          <td className={`pnlg-amt${neg ? ' pnlg-neg' : ''}${col.highlight ? ' pnlg-now' : ''}${isTotal ? ' pnlg-total-col' : ''}`}>
            {cell.actual == null ? '—' : money(cell.actual)}
          </td>
          <td className={`pnlg-pct${col.highlight ? ' pnlg-now' : ''}${isTotal ? ' pnlg-total-col' : ''}`}>
            {cell.actual == null ? '—' : <PctChip value={cell.pctRev} />}
          </td>
        </React.Fragment>
      );
    });
  };

  return (
    <div className="pnlg-scroll">
      <table className="pnlg-table">
        <thead>
          {/* Row 1: Description + column group headers */}
          <tr>
            <th className="pnlg-desc-th" rowSpan={2}>Description</th>
            {columns.map((col, cIdx) => (
              <th
                key={cIdx}
                className={`pnlg-mon-th${col.highlight ? ' pnlg-mon-now' : ''}${col.isTotal ? ' pnlg-total-col pnlg-total-th' : ''}`}
                colSpan={2}
              >
                {col.label}
              </th>
            ))}
          </tr>
          {/* Row 2: RM / % sub-labels */}
          <tr>
            {columns.map((col, cIdx) => (
              <React.Fragment key={cIdx}>
                <th className={`pnlg-sub-th pnlg-amt-th${col.highlight ? ' pnlg-now' : ''}${col.isTotal ? ' pnlg-total-col' : ''}`}>RM</th>
                <th className={`pnlg-sub-th pnlg-pct-th${col.highlight ? ' pnlg-now' : ''}${col.isTotal ? ' pnlg-total-col' : ''}`}>%</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {search.trim() && !rows.some(row=>row.kind!=='section') && <tr><td colSpan={totalCols}>No accounts match “{search}”.</td></tr>}
          {rows.map((row) => {
            if (row.kind === 'section') {
              return (
                <tr key={row.key} className="pnlg-section">
                  <td colSpan={totalCols}>{row.label}</td>
                </tr>
              );
            }

            if (row.kind === 'category') {
              return (
                <tr key={row.key} className={`pnlg-cat${row.open ? ' pnlg-cat-open' : ''}`}>
                  <td className="pnlg-desc">
                    {row.hasChildren ? (
                      <button type="button" className="pnlg-expand" aria-expanded={expandedKeys.has(row.key)} onClick={() => toggleExpand(row.key)}>
                        <span className="pnlg-chev">{expandedKeys.has(row.key) ? '▾' : '▸'}</span>
                        {row.label}
                      </button>
                    ) : row.label}
                  </td>
                  {renderDataCells(row)}
                </tr>
              );
            }

            if (row.kind === 'child') {
              return (
                <tr key={row.key} className={`pnlg-child${row.last ? ' pnlg-child-last' : ''}`}>
                  <td className="pnlg-desc pnlg-child-desc"><span className="pnlg-child-label">{row.label}</span></td>
                  {columns.map((col, cIdx) => {
                    const cell = getCell(cIdx, row.key);
                    const neg = isNegVal(cell.actual);
                    return (
                      <React.Fragment key={cIdx}>
                        <td className={`pnlg-amt pnlg-child-amt${neg ? ' pnlg-neg' : ''}${col.highlight ? ' pnlg-now' : ''}${col.isTotal ? ' pnlg-total-col' : ''}`}>
                          {cell.actual == null ? '—' : money(cell.actual)}
                        </td>
                        <td className={`pnlg-pct pnlg-child-pct${col.highlight ? ' pnlg-now' : ''}${col.isTotal ? ' pnlg-total-col' : ''}`}>
                          {cell.actual == null ? '—' : <PctChip value={cell.pctRev} />}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            }

            if (row.kind === 'input') {
              return (
                <tr key={row.key} className="pnlg-input">
                  <td className="pnlg-desc">{row.label}</td>
                  {renderDataCells(row)}
                </tr>
              );
            }

            if (row.kind === 'computed') {
              const isGP = row.key === 'gross_profit';
              const isBig = BIG_KEYS.has(row.key);
              let rowCls = 'pnlg-tot';
              if (isBig) rowCls += ' pnlg-tot-big';
              if (isGP) rowCls += ' pnlg-gp';
              if (row.key === 'ebitda') rowCls += ' pnlg-ebitda';
              if (row.key === 'pbt') rowCls += ' pnlg-pbt';
              if (row.key === 'pat') rowCls += ' pnlg-pat';
              if (row.key === 'tax') rowCls += ' pnlg-tax';

              return (
                <tr key={row.key} className={rowCls}>
                  <td className="pnlg-desc">{row.label}</td>
                  {renderDataCells(row)}
                </tr>
              );
            }

            return null;
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ---- Period bar (module scope — stable identity across renders) ---- */
function PeriodBar({ period, setPeriod, viewMode, onDownload, canDownload, breakdown, setBreakdown }) {
  const viewLabel = viewMode === 'detailed' ? 'Detailed report' : viewMode === 'compare' ? 'Period comparison' : 'Management summary';
  return (
    <div className="pnl-header">
      <div className="pnl-title-group"><h2 className="pnl-title">{viewLabel}</h2><p>Review actual performance and budget variance.</p></div>
      <div className="pnl-header-actions">
        {viewMode !== 'compare' && (
          <div className="pnl-period-bar" aria-label="Reporting period">
            {[['monthly', 'Monthly'], ['quarterly', 'Quarterly'], ['ytd', 'YTD'], ['yearly', 'Yearly']].map(([p, l]) => (
              <button key={p} type="button" aria-pressed={period === p} className={`pnl-period-btn ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>{l}</button>
            ))}
          </div>
        )}
        {/* The simple view's PDF is a fixed waterfall with no categories, so the
            breakdown toggle only applies to the detailed and comparison views. */}
        {viewMode !== 'simple' && (
          <label className="pnl-brk" title="Include each category's line-item breakdown in the PDF">
            <input type="checkbox" checked={breakdown} onChange={(e) => setBreakdown(e.target.checked)} />
            Include breakdown
          </label>
        )}
        <button type="button" className="pnl-pdf-btn" onClick={onDownload} disabled={!canDownload} title="Download this view as PDF">
          <Download size={15} aria-hidden="true" /> Export PDF
        </button>
      </div>
    </div>
  );
}

/* ======================================================
   MAIN COMPONENT
   ====================================================== */
export default function PnLStatement({ month, view = 'simple' }) {
  const [period, setPeriod] = useState('monthly');
  const viewMode = view === 'detailed' ? 'detailed' : view === 'compare' ? 'compare' : 'simple';   // driven by sidebar route
  const [quarter, setQuarter] = useState(getCurrentQuarter());
  const [gridColumns, setGridColumns] = useState(null);
  const [gridLoading, setGridLoading] = useState(false);
  const [gridError, setGridError] = useState(null);
  // PDF exports category totals only by default; tick to include the children.
  const [pdfBreakdown, setPdfBreakdown] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');

  const year = month ? month.split('-')[0] : new Date().getFullYear().toString();
  const selectedMonthNum = month ? parseInt(month.split('-')[1], 10) : new Date().getMonth() + 1; // 1-based

  // comparison mode: two months A vs B (default = previous month vs selected month)
  const baseYM = `${year}-${String(selectedMonthNum).padStart(2, '0')}`;
  const [cmpB, setCmpB] = useState(baseYM);
  const [cmpA, setCmpA] = useState(prevMonth(baseYM));
  const cmpYears = (() => { const yn = parseInt(year, 10); return [yn - 3, yn - 2, yn - 1, yn]; })();

  /* ---- Download the current view as a PDF (rebuilt from data, crisp text) ---- */
  const handleDownloadPdf = useCallback(() => {
    if (!gridColumns || !gridColumns.length) return;
    const structureData = gridColumns.map(c => c.data).find(d => d && d.sections);
    if (!structureData) return;

    // Expand every category only when the breakdown was requested; otherwise the
    // PDF carries category totals alone. Applies to both the detailed and the
    // comparison export, which share this row list.
    const expandForPdf = new Set();
    if (pdfBreakdown) {
      for (const s of (structureData.sections || [])) {
        for (const line of (s.lines || [])) {
          if (line.children && line.children.length) expandForPdf.add(line.key);
        }
      }
    }
    const rows = buildRowList(viewMode === 'detailed' ? actualStatementSections(gridColumns, structureData.sections) : structureData.sections, expandForPdf);
    const lookups = gridColumns.map(c => buildColLookup(c.data));

    const periodLabel = { monthly: 'Monthly', quarterly: `${quarter} ${year}`, ytd: 'Year to Date', yearly: 'Yearly' }[period] || period;
    const suffix = period === 'quarterly' ? `${quarter}_` : period === 'monthly' ? `${MONTH_NAMES[selectedMonthNum - 1]}_` : '';

    // ---- Comparison view → A vs B + variance PDF ----
    if (viewMode === 'compare') {
      const [colA, colB] = gridColumns;
      const la = lookups[0], lb = lookups[1];
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      drawRowWashComparison(doc, {
        org: 'Runningmen Group',
        labelA: colA.label,
        labelB: colB.label,
        generatedOn: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
        rows,
        lookupA: la,
        lookupB: lb,
        // Shares are recomputed from each month's own total revenue rather than
        // reusing pctRev, so a line is always measured against its own month.
        revenueA: Number((la.total_revenue || {}).actual) || 0,
        revenueB: Number((lb.total_revenue || {}).actual) || 0,
      });
      doc.save(`P&L_Compare_${cmpA}_vs_${cmpB}.pdf`);
      return;
    }

    // ---- Simple view → compact single-column PDF ----
    if (viewMode === 'simple') {
      const primary = pickPrimaryColumn(gridColumns);
      const lines = primary ? buildSimpleLines(primary.data) : null;
      if (!lines) return;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      doc.setFontSize(14); doc.setTextColor(30); doc.text('Profit & Loss Statement', 40, 40);
      doc.setFontSize(9); doc.setTextColor(120); doc.text(`Simple view · ${primary ? primary.label : periodLabel}`, 40, 56);
      autoTable(doc, {
        startY: 70, theme: 'plain',
        head: [['Account', 'Actual (RM)', 'Budget (RM)', 'Variance', '% Rev']],
        body: lines.map(line => [
          (line.kind === 'ded' ? '   ' : '') + line.label,
          signedSimpleValue(line, 'amount') == null ? '—' : money(signedSimpleValue(line, 'amount')),
          signedSimpleValue(line, 'budget') == null ? '—' : money(signedSimpleValue(line, 'budget')),
          deltaMoney(simpleVariance(line)),
          line.pct == null ? '—' : pct(line.kind === 'ded' ? Math.abs(line.pct) : line.pct),
        ]),
        styles: { fontSize: 10, cellPadding: 6 },
        headStyles: { fillColor: [43, 40, 26], textColor: 255, fontStyle: 'bold', halign: 'right' },
        columnStyles: { 0: { halign: 'left', cellWidth: 190 }, 1: { halign: 'right', cellWidth: 88 }, 2: { halign: 'right', cellWidth: 88 }, 3: { halign: 'right', cellWidth: 80 }, 4: { halign: 'right', cellWidth: 60 } },
        didParseCell: (d) => {
          if (d.section !== 'body') return;
          const l = lines[d.row.index];
          if (d.column.index === 0) d.cell.styles.halign = 'left'; else d.cell.styles.halign = 'right';
          if (l.kind === 'key' || l.kind === 'sub') d.cell.styles.fontStyle = 'bold';
          if (l.kind === 'final') {
            const isLoss = signedSimpleValue(l, 'amount') < 0;
            d.cell.styles.fontStyle = 'bold';
            d.cell.styles.fillColor = isLoss ? [255, 241, 239] : [225, 244, 230];
            d.cell.styles.textColor = isLoss ? [143, 66, 60] : [20, 110, 60];
            d.cell.styles.fontSize = 12;
          }
          if (d.column.index === 3) {
            const variance = simpleVariance(l);
            if (variance > 0) d.cell.styles.textColor = [31, 107, 52];
            if (variance < 0) d.cell.styles.textColor = [180, 63, 76];
          }
          if (l.kind !== 'ded' && (d.column.index === 1 || d.column.index === 2) && typeof d.cell.raw === 'string' && d.cell.raw.startsWith('(')) d.cell.styles.textColor = [180, 63, 76];
        },
      });
      doc.save(`P&L_Simple_${suffix}${year}.pdf`);
      return;
    }

    // ---- Single column (the monthly view) → portrait row-wash statement ----
    // The row wash reads a line against its own month's revenue, so it only makes
    // sense with one column. Multi-column periods keep the landscape grid below.
    if (gridColumns.length === 1) {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const monthName = FULL_MONTHS[selectedMonthNum - 1];
      const lastDay = new Date(Number(year), selectedMonthNum, 0).getDate();
      const gen = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      const isMonth = period === 'monthly';
      drawRowWashStatement(doc, {
        org: 'Runningmen Group',
        periodLabel: isMonth ? `${monthName} ${year}` : (gridColumns[0].label || periodLabel),
        periodSub: isMonth ? `Month ended ${lastDay} ${monthName} ${year}` : '',
        generatedOn: gen,
        rows,
        lookup: lookups[0] || {},
      });
      doc.save(`P&L_${suffix}${year}.pdf`);
      return;
    }

    // ---- Multi-column periods (quarter, YTD, year) -> landscape grid ----
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    drawPeriodGrid(doc, {
      org: 'Runningmen Group',
      periodLabel: `${periodLabel}${period === 'monthly' ? ` ${year}` : ''}`,
      generatedOn: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
      columns: gridColumns.map(c => ({ label: c.label, isTotal: !!c.isTotal })),
      lookups,
      rows,
    });

    doc.save(`P&L_${suffix}${year}.pdf`);
  }, [gridColumns, period, year, quarter, selectedMonthNum, viewMode, cmpA, cmpB, pdfBreakdown]);

  /* ---- Fetch helper ---- */
  const fetchJSON = useCallback(async (url) => {
    const token = localStorage.getItem('token');
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return null;
    return res.json().catch(() => null);
  }, []);

  /* ---- Build columns for MONTHLY: only the selected month ---- */
  const fetchMonthlyColumns = useCallback(async () => {
    if (!month) return;
    setGridLoading(true);
    setGridError(null);
    try {
      const mm = String(selectedMonthNum).padStart(2, '0');
      const data = await fetchJSON(`${API}/api/pnl?period=monthly&month=${year}-${mm}`);
      setGridColumns([{
        label: `${MONTH_NAMES[selectedMonthNum - 1]} ${year}`,
        data,
        highlight: true,
        isTotal: false,
      }]);
    } catch (err) {
      setGridError(err.message);
    } finally {
      setGridLoading(false);
    }
  }, [month, year, selectedMonthNum, fetchJSON]);

  /* ---- Build columns for QUARTERLY: 3 months + total ---- */
  const fetchQuarterlyColumns = useCallback(async () => {
    setGridLoading(true);
    setGridError(null);
    try {
      // Determine the 3 months of this quarter
      const qIdx = QUARTERS.indexOf(quarter); // 0-based (Q1=0, Q2=1, ...)
      const startMonth = qIdx * 3 + 1; // 1,4,7,10
      const monthPromises = [];
      for (let m = startMonth; m < startMonth + 3; m++) {
        const mm = String(m).padStart(2, '0');
        monthPromises.push(fetchJSON(`${API}/api/pnl?period=monthly&month=${year}-${mm}`));
      }
      const totalPromise = fetchJSON(`${API}/api/pnl?period=quarterly&quarter=${quarter}&year=${year}`);
      const [m1, m2, m3, totalData] = await Promise.all([...monthPromises, totalPromise]);
      const monthResults = [m1, m2, m3];
      const cols = monthResults.map((data, idx) => ({
        label: MONTH_NAMES[startMonth - 1 + idx],
        data,
        highlight: false,
        isTotal: false,
      }));
      cols.push({
        label: `${quarter} ${year}`,
        data: totalData,
        highlight: true,
        isTotal: true,
      });
      setGridColumns(cols);
    } catch (err) {
      setGridError(err.message);
    } finally {
      setGridLoading(false);
    }
  }, [quarter, year, fetchJSON]);

  /* ---- Build columns for YTD: Jan..currentMonth + total ---- */
  const fetchYTDColumns = useCallback(async () => {
    setGridLoading(true);
    setGridError(null);
    try {
      const curMonth = selectedMonthNum; // use same month as monthly view
      const monthPromises = [];
      for (let m = 1; m <= curMonth; m++) {
        const mm = String(m).padStart(2, '0');
        monthPromises.push(fetchJSON(`${API}/api/pnl?period=monthly&month=${year}-${mm}`));
      }
      const totalPromise = fetchJSON(`${API}/api/pnl?period=ytd&year=${year}`);
      const results = await Promise.all([...monthPromises, totalPromise]);
      const totalData = results[results.length - 1];
      const monthResults = results.slice(0, curMonth);
      const cols = monthResults.map((data, idx) => ({
        label: MONTH_NAMES[idx],
        data,
        highlight: false,
        isTotal: false,
      }));
      cols.push({
        label: `YTD (Jan–${MONTH_NAMES[curMonth - 1]})`,
        data: totalData,
        highlight: true,
        isTotal: true,
      });
      setGridColumns(cols);
    } catch (err) {
      setGridError(err.message);
    } finally {
      setGridLoading(false);
    }
  }, [year, selectedMonthNum, fetchJSON]);

  /* ---- Build columns for YEARLY: Jan..Dec + total ---- */
  const fetchYearlyColumns = useCallback(async () => {
    setGridLoading(true);
    setGridError(null);
    try {
      const monthPromises = [];
      for (let m = 1; m <= 12; m++) {
        const mm = String(m).padStart(2, '0');
        monthPromises.push(fetchJSON(`${API}/api/pnl?period=monthly&month=${year}-${mm}`));
      }
      const totalPromise = fetchJSON(`${API}/api/pnl?period=yearly&year=${year}`);
      const results = await Promise.all([...monthPromises, totalPromise]);
      const totalData = results[results.length - 1];
      const monthResults = results.slice(0, 12);
      const cols = monthResults.map((data, idx) => ({
        label: MONTH_NAMES[idx],
        data,
        highlight: false,
        isTotal: false,
      }));
      cols.push({
        label: 'Year',
        data: totalData,
        highlight: true,
        isTotal: true,
      });
      setGridColumns(cols);
    } catch (err) {
      setGridError(err.message);
    } finally {
      setGridLoading(false);
    }
  }, [year, fetchJSON]);

  /* ---- Build columns for COMPARISON: two months A vs B ---- */
  const fetchComparisonColumns = useCallback(async () => {
    setGridLoading(true);
    setGridError(null);
    try {
      const [aData, bData] = await Promise.all([
        fetchJSON(`${API}/api/pnl?period=monthly&month=${cmpA}`),
        fetchJSON(`${API}/api/pnl?period=monthly&month=${cmpB}`),
      ]);
      setGridColumns([
        { label: monthLabel(cmpA), data: aData, highlight: false, isTotal: false },
        { label: monthLabel(cmpB), data: bData, highlight: true, isTotal: false },
      ]);
    } catch (err) {
      setGridError(err.message);
    } finally {
      setGridLoading(false);
    }
  }, [cmpA, cmpB, fetchJSON]);

  /* ---- Effects: fetch on period/quarter/month/view change ---- */
  useEffect(() => {
    setGridColumns(null);
    if (viewMode === 'compare') {
      fetchComparisonColumns();
    } else if (period === 'monthly') {
      fetchMonthlyColumns();
    } else if (period === 'quarterly') {
      fetchQuarterlyColumns();
    } else if (period === 'ytd') {
      fetchYTDColumns();
    } else if (period === 'yearly') {
      fetchYearlyColumns();
    }
  }, [viewMode, period, quarter, month, cmpA, cmpB, fetchComparisonColumns, fetchMonthlyColumns, fetchQuarterlyColumns, fetchYTDColumns, fetchYearlyColumns]);

  const handleRetry = () => {
    if (viewMode === 'compare') fetchComparisonColumns();
    else if (period === 'monthly') fetchMonthlyColumns();
    else if (period === 'quarterly') fetchQuarterlyColumns();
    else if (period === 'ytd') fetchYTDColumns();
    else if (period === 'yearly') fetchYearlyColumns();
  };

  if (viewMode === 'detailed') return <ClearPnlWorkspace columns={gridError ? null : gridColumns} loading={gridLoading} search={accountSearch} setSearch={setAccountSearch}
    controls={<PeriodBar period={period} setPeriod={setPeriod} viewMode={viewMode} onDownload={handleDownloadPdf} canDownload={!gridLoading && !gridError && !!gridColumns} breakdown={pdfBreakdown} setBreakdown={setPdfBreakdown} />}>
    {period === 'quarterly' && <div className="pnl-quarter-bar">{QUARTERS.map(q=><button key={q} className={`pnl-quarter-chip ${quarter===q?'active':''}`} onClick={()=>setQuarter(q)}>{q}</button>)}</div>}
    {gridLoading && <div className="pnl-loading"><div className="pnl-spinner"/><p>Loading P&amp;L…</p></div>}
    {gridError && <div className="pnl-error" role="alert"><p>{gridError}</p><button onClick={handleRetry}>Retry</button></div>}
    {!gridLoading && !gridError && gridColumns && <PeriodGrid columns={gridColumns} search={accountSearch}/>}
  </ClearPnlWorkspace>;

  return (
    <div className="pnl-container">
      <PeriodBar period={period} setPeriod={setPeriod} viewMode={viewMode} onDownload={handleDownloadPdf} canDownload={!gridLoading && !gridError && !!gridColumns} breakdown={pdfBreakdown} setBreakdown={setPdfBreakdown} />

      {viewMode !== 'compare' && period === 'quarterly' && (
        <div className="pnl-quarter-bar">
          {QUARTERS.map(q => (
            <button key={q} className={`pnl-quarter-chip ${quarter === q ? 'active' : ''}`} onClick={() => setQuarter(q)}>{q}</button>
          ))}
        </div>
      )}

      {viewMode === 'compare' && (
        <div className="cmp-bar">
          <span className="cmp-lbl">Compare</span>
          <MonthYearPicker value={cmpA} onChange={setCmpA} years={cmpYears} />
          <button className="cmp-swap" title="Swap months" onClick={() => { const a = cmpA; setCmpA(cmpB); setCmpB(a); }}>⇄</button>
          <MonthYearPicker value={cmpB} onChange={setCmpB} years={cmpYears} />
          <span className="cmp-hint">Variance = <b>{monthLabel(cmpB)}</b> − <b>{monthLabel(cmpA)}</b></span>
        </div>
      )}

      {gridLoading && (
        <div className="pnl-loading"><div className="pnl-spinner" /><p>Loading P&amp;L…</p></div>
      )}
      {gridError && (
        <div className="pnl-error">
          <p>Error: {gridError}</p>
          <button onClick={handleRetry}>Retry</button>
        </div>
      )}
      {!gridLoading && !gridError && gridColumns && (
        viewMode === 'simple'
          ? <><KpiCards primary={pickPrimaryColumn(gridColumns)} /><div className="pnlg-card pnl-simple-card"><SimplePnl columns={gridColumns} /></div></>
          : viewMode === 'compare'
            ? (gridColumns.length >= 2
                ? <div className="pnlg-card"><ComparisonGrid colA={gridColumns[0]} colB={gridColumns[1]} /></div>
                : <div className="pnl-loading"><div className="pnl-spinner" /><p>Loading comparison…</p></div>)
            : <div className="pnlg-card"><PeriodGrid columns={gridColumns} /></div>
      )}
    </div>
  );
}
